import React, { useCallback, useMemo } from "react";
import { formatISO } from "date-fns";
import { ExpandedState } from "@tanstack/react-table";
import { ReportFormat } from "@common/helpers/reports";
import { ArAgingBucket } from "@common/types/reports/ar-aging";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import { Button } from "@design-system/Button/Button";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useOverlays } from "contexts/OverlayContext";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { CollapseIcon, ExpandIcon } from "@icons";
import { useArAgingDetailQuery } from "@api/accountingReports.api";
import { DownloadDropdown } from "@components/accounting/balanceSheet/DownloadDropdown";
import { AccountingReportFiltersForm } from "../AccountingReportFiltersForm";
import { useAccountingTabContext } from "../useAccountingTab";
import { useExpandAllState } from "../useExpandAllState";
import { ArAgingAsOfDateFilter } from "./ArAgingAsOfDateFilter";
import { ArAgingDetailContent } from "./ArAgingDetailContent";
import {
    getArAgingDetailExpandedState,
    getArAgingDetailInvoiceIds,
} from "./arAgingDetailViewBuilder";
import { useArAgingEntitySelection } from "./useArAgingEntitySelection";
import { useArAgingDetailDownload } from "./useArAgingDetailDownload";
import { arAgingDetailFilterDefinition } from "../accountingReportFilterDefinitions";

interface Props {
    buckets?: ArAgingBucket[];
    onBucketsChange?: (buckets: ArAgingBucket[] | undefined) => void;
    showGoToInvoices?: boolean;
}

export const ArAgingDetailReport: React.FC<Props> = ({
    buckets,
    onBucketsChange,
    showGoToInvoices,
}) => {
    const { openOverlay } = useOverlays();
    const { filters, setFilters } = useAccountingTabContext();
    const { selectedEntityIds, isEntityDisabled, clearedEntityIdsFallback } =
        useArAgingEntitySelection(filters);

    const reportFilters = {
        ...filters,
        entityIds: selectedEntityIds,
    };

    const { arAgingDetail, generationDate } = useArAgingDetailQuery(
        reportFilters,
        buckets,
    );

    const { isDownloading, download } = useArAgingDetailDownload(
        reportFilters,
        buckets,
    );

    const handleDownload = useCallback(
        (format: ReportFormat) => {
            const asOfDate =
                arAgingDetail.data?.asOfDate ??
                formatISO(new Date(), { representation: "date" });
            download({ format, asOfDate });
        },
        [download, arAgingDetail.data?.asOfDate],
    );

    const detailRows = arAgingDetail.data;
    const expandAllState = useMemo<ExpandedState>(() => {
        if (!detailRows) {
            return {};
        }
        return getArAgingDetailExpandedState(
            detailRows.sections.map((section) => ({
                id: `bucket:${section.bucket}`,
                rowKind: "bucket" as const,
            })),
        );
    }, [detailRows]);

    const {
        expanded,
        setExpanded,
        isCollapsed,
        toggleAll: handleToggleCollapse,
    } = useExpandAllState(expandAllState);

    const invoiceIds = useMemo(
        () => (detailRows ? getArAgingDetailInvoiceIds(detailRows) : []),
        [detailRows],
    );
    const handleGoToInvoices = useCallback(() => {
        openOverlay("invoicesDrilldown", { filters: { ids: invoiceIds } });
    }, [openOverlay, invoiceIds]);

    const goToInvoicesFooter =
        showGoToInvoices && invoiceIds.length > 0 ? (
            <CommandIsland
                sticky
                alignEnd
                className={commandIslandStyles.bulkActionsStickyFooter}
            >
                <Button
                    variant="primary"
                    size="md"
                    onClick={handleGoToInvoices}
                >
                    Go to invoices
                </Button>
            </CommandIsland>
        ) : undefined;

    return (
        <>
            <AccountingReportFiltersForm
                filters={filters}
                setFilters={setFilters}
                hideSearch
                hideLedgerSelect
                hideCreateJournalEntryButton
                hideDateRangeFilter
                {...arAgingDetailFilterDefinition.filterProps}
                dateFilterOverride={
                    <ArAgingAsOfDateFilter
                        value={filters.asOfDate}
                        onChange={(asOfDate) =>
                            setFilters((current) => ({
                                ...current,
                                asOfDate,
                            }))
                        }
                    />
                }
                entityFilterOverride={
                    <EntityFilterCombobox
                        filters={{ entityIds: selectedEntityIds }}
                        onlyBusiness
                        excludeAllMockEntities
                        isEntityDisabled={isEntityDisabled}
                        disabledEntityTooltip="This entity has no accrual ledger."
                        onChange={(newFilters) =>
                            setFilters((current) => ({
                                ...current,
                                entityIds:
                                    newFilters.entityIds &&
                                    newFilters.entityIds.length > 0
                                        ? newFilters.entityIds
                                        : clearedEntityIdsFallback,
                            }))
                        }
                    />
                }
                showAgingPeriodFilter={!!onBucketsChange}
                agingBuckets={buckets}
                onAgingBucketsChange={onBucketsChange}
                additionalButtons={
                    <>
                        <Tooltip
                            key={isCollapsed ? "expand" : "collapse"}
                            content={
                                isCollapsed ? "Expand all" : "Collapse all"
                            }
                        >
                            <Button
                                iconOnly
                                variant="primary"
                                size="md"
                                onClick={handleToggleCollapse}
                                aria-label={
                                    isCollapsed ? "Expand all" : "Collapse all"
                                }
                            >
                                {isCollapsed ? (
                                    <ExpandIcon />
                                ) : (
                                    <CollapseIcon />
                                )}
                            </Button>
                        </Tooltip>
                        <DownloadDropdown
                            onDownload={handleDownload}
                            loading={isDownloading}
                            disabled={
                                arAgingDetail.isLoading ||
                                !arAgingDetail.isSuccess
                            }
                        />
                    </>
                }
            />
            <LoaderOnInitialLoading
                queryResult={arAgingDetail}
                dimWhileFetching
            >
                {arAgingDetail.data && (
                    <ArAgingDetailContent
                        data={arAgingDetail.data}
                        filters={reportFilters}
                        chrome={arAgingDetail.data.presentation.chrome}
                        numberFormat={
                            arAgingDetail.data.presentation.numberFormat
                        }
                        generationDate={generationDate}
                        expanded={expanded}
                        onExpandedChange={setExpanded}
                        footer={goToInvoicesFooter}
                    />
                )}
            </LoaderOnInitialLoading>
        </>
    );
};
